import { useEffect, useState } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";

import auth from "src/apiService/auth";
import type { AppState } from "src/store";
import {
  initialSalesTransactionsSyncCompleteSelector,
  salesTransactionsFirestorePendingSelector,
} from "src/store/userDataLoad/selector";
import { logOut } from "src/store/system/actions";

import { InitialFullPageDebounceMs } from "./salesTransactionsLoaderConstants";
import { useSalesTransactionsDataLoaderContext } from "./SalesTransactionsDataLoaderContext";

export interface SalesTransactionsLoaderUi {
  /** Block the private shell with AppLoader. */
  shellBlockingLoader: boolean;
}

/**
 * Windowed Firestore listeners for Sales and Transactions once `useUserDataLoader` reports
 * `isCoreUserDataReady` (auth, claims, user doc snapshot gate — not auxiliary collections).
 * Sync scheduling lives on `DataLoaderManager` (`setCoreDataReady` + Redux subscription).
 */
export function useSalesTransactionsDataLoader(
  coreDataReady: boolean
): SalesTransactionsLoaderUi {
  const dispatch = useDispatch();
  const store = useStore<AppState>();
  const { getManager } = useSalesTransactionsDataLoaderContext();
  const pending = useSelector(salesTransactionsFirestorePendingSelector);
  const initialDone = useSelector(initialSalesTransactionsSyncCompleteSelector);

  const [delayedFullPage, setDelayedFullPage] = useState(false);

  useEffect(() => {
    const unsubscribe = auth().onAuthStateChanged(
      (user) => {
        if (!user && store.getState().system.loggedIn) {
          dispatch(logOut());
        }
      },
      (error) => {
        console.error("Auth state listener error", error);
        dispatch(logOut());
      }
    );
    return () => unsubscribe();
  }, [dispatch, store]);

  useEffect(() => {
    const manager = getManager();
    if (!manager) return;
    manager.setCoreDataReady(coreDataReady);
    return () => {
      manager.setCoreDataReady(false);
    };
  }, [coreDataReady, getManager]);

  useEffect(() => {
    if (!pending || !initialDone) {
      setDelayedFullPage(false);
      return;
    }
    const id = window.setTimeout(() => {
      setDelayedFullPage(true);
    }, InitialFullPageDebounceMs);
    return () => window.clearTimeout(id);
  }, [pending, initialDone]);

  /** Block while core user data is loading, until first sales/tx sync completes, or while a
   * debounced full-page backfill is in progress (`initialDone && pending && delayedFullPage`).
   * The `!initialDone` branch covers the window before Firestore sets `pending` (manager debounce).
   */
  const shellBlockingLoader =
    !coreDataReady ||
    !initialDone ||
    (initialDone && pending && delayedFullPage);

  return {
    shellBlockingLoader,
  };
}
